import { create } from 'zustand';
import { useAuthStore } from '@/store/auth-store';
import { sessionScope } from '@/features/auth/session-scope';
import { useVoiceSessionStore } from './voice-session-store';

/**
 * Thin, shared slice of the live voice-room state + imperative controls.
 *
 * `VoiceRoom` owns the actual LiveKit `Room` (audio, tracks, the expanded
 * overlay). It *publishes* this compact status — including the connection
 * lifecycle (`connectionState`) — and *registers* its controls here so the
 * global `CommsHub` can render THE one minimized pill (connecting /
 * reconnecting / error / live) + the quick mute/“in voice” bolitas WITHOUT
 * owning the room (TC-33: VoiceRoom no longer paints its own floating pill).
 * Cleared when the room unmounts (leave) and on logout.
 */
export type VoiceConnectionState = 'connecting' | 'live' | 'reconnecting' | 'error';

export interface VoiceRoomControls {
  toggleMic: () => void;
  toggleDeafen: () => void;
  /** Re-open the full room overlay (un-minimize). */
  expand: () => void;
  /** Leave the session entirely (disconnect + clear). */
  leave: () => void;
}

interface VoiceRoomState {
  /** Where the LiveKit connection is in its lifecycle. */
  connectionState: VoiceConnectionState;
  /** Human-readable failure reason; only meaningful when connectionState === 'error'. */
  errorMessage?: string;
  micEnabled: boolean;
  deafened: boolean;
  /** People currently in the room (local + remotes). */
  total: number;
  channelName?: string;
  /** Wired by VoiceRoom while connected; null when there's no live room. */
  controls: VoiceRoomControls | null;
  setStatus: (
    s: Partial<
      Pick<
        VoiceRoomState,
        'connectionState' | 'errorMessage' | 'micEnabled' | 'deafened' | 'total' | 'channelName'
      >
    >,
  ) => void;
  setControls: (c: VoiceRoomControls | null) => void;
  reset: () => void;
}

const INITIAL = {
  connectionState: 'connecting' as VoiceConnectionState,
  errorMessage: undefined as string | undefined,
  micEnabled: true,
  deafened: false,
  total: 0,
  channelName: undefined as string | undefined,
  controls: null as VoiceRoomControls | null,
};

export const useVoiceRoomStore = create<VoiceRoomState>((set) => ({
  ...INITIAL,
  setStatus: (s) => set(s),
  setControls: (controls) => set({ controls }),
  reset: () => set(INITIAL),
}));

useAuthStore.subscribe((next,previous)=>{if(sessionScope(next)!==sessionScope(previous))useVoiceRoomStore.getState().reset();});
useVoiceSessionStore.subscribe((next,previous)=>{if(next.revision!==previous.revision)useVoiceRoomStore.getState().reset();});
